import React, { useMemo, createContext, useContext, useState, useEffect, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import _styled, { keyframes } from 'styled-components';
import { rgba } from 'polished';
import { useDispatch, useSelector, Provider } from 'react-redux';
import intl from 'react-intl-universal';
import { useQuery } from 'react-query';
import { createAsyncThunk, createSlice, createEntityAdapter, configureStore, combineReducers, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';
import isEmpty from 'lodash-es/isEmpty';
import groupBy from 'lodash-es/groupBy';
import { RotateSpinLoader } from 'react-css-loaders';

var Session = /*#__PURE__*/createContext(undefined);

var SessionProvider = function SessionProvider(_ref) {
  var session = _ref.session,
      children = _ref.children;
  var value = useMemo(function () {
    return session;
  }, [session]);
  return React.createElement(Session.Provider, {
    value: value
  }, children);
};

var useSession = function useSession() {
  var session = useContext(Session);

  if (session === undefined) {
    throw new Error("'useSession' must be used within a SessionProvider");
  }

  return session;
};

var useSession$1 = (function (name) {
  if (name === void 0) {
    name = 'X-ODDS-SESSION';
  }

  return document.cookie.split('; ').reduce(function (r, v) {
    var parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');
});

var IsMobileContext = /*#__PURE__*/createContext(undefined);

var IsMobileProvider = function IsMobileProvider(_ref) {
  var children = _ref.children,
      isMobile = _ref.isMobile;
  return React.createElement(IsMobileContext.Provider, {
    value: isMobile
  }, children);
};

var useIsMobileContext = function useIsMobileContext() {
  var context = useContext(IsMobileContext);

  if (context === undefined) {
    throw new Error("'useIsMobileContext' must be used within a IsMobileProvider");
  }

  return context;
};

var useIsMobile = function useIsMobile() {
  return useIsMobileContext();
};

var IsAuthContext = /*#__PURE__*/createContext(undefined);

var IsAuthProvider = function IsAuthProvider(_ref) {
  var children = _ref.children,
      isAuth = _ref.isAuth;
  return React.createElement(IsAuthContext.Provider, {
    value: isAuth
  }, children);
};

var useIsAuthContext = function useIsAuthContext() {
  var context = useContext(IsAuthContext);

  if (context === undefined) {
    throw new Error("'useIsAuthContext' must be used within a IsAuthProvider");
  }

  return context;
};

var useIsAuth = function useIsAuth() {
  return useIsAuthContext();
};

var IsOpenContext = /*#__PURE__*/createContext(undefined);
var SetIsOpenContext = /*#__PURE__*/createContext(undefined);

var NotificationProvider = function NotificationProvider(_ref) {
  var children = _ref.children;

  var _useState = useState(false),
      isOpen = _useState[0],
      setIsOpen = _useState[1];

  return React.createElement(IsOpenContext.Provider, {
    value: isOpen
  }, React.createElement(SetIsOpenContext.Provider, {
    value: setIsOpen
  }, children));
};

var useIsOpenContext = function useIsOpenContext() {
  var context = useContext(IsOpenContext);

  if (context === undefined) {
    throw new Error("'useIsOpenContext' must be used within a NotificationProvider");
  }

  return context;
};

var open = function open() {
  var dispatch = useContext(SetIsOpenContext);

  if (dispatch === undefined) {
    throw new Error("'open [Notification]' must be used within a NotificationProvider");
  }

  return function () {
    return dispatch(true);
  };
};

var close = function close() {
  var dispatch = useContext(SetIsOpenContext);

  if (dispatch === undefined) {
    throw new Error("'close [Notification]' must be used within a NotificationProvider");
  }

  return function () {
    return dispatch(false);
  };
};

var useNotification = function useNotification() {
  return {
    isOpen: useIsOpenContext(),
    open: open(),
    close: close()
  };
};

var MOUSEDOWN = 'mousedown';
var TOUCHSTART = 'touchstart';
var events = [MOUSEDOWN, TOUCHSTART];

function useOnClickOutside(ref, handler) {
  useEffect(function () {
    var listener = function listener(event) {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }

      handler(event);
    };

    events.forEach(function (event) {
      document.addEventListener(event, listener);
    });
    return function () {
      events.forEach(function (event) {
        document.removeEventListener(event, listener);
      });
    };
  }, [ref, handler]);
}

var breakpoints = {
  xl: '1024px'
};
var devices = {
  mobile: "(max-width: " + breakpoints.xl + ")"
};

var Notification = /*#__PURE__*/_styled(motion.aside).withConfig({
  displayName: "indexstyled__Notification",
  componentId: "bhctxx-0"
})(["background-color:", ";bottom:0;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:16px;left:0;pointer-events:none;position:fixed;right:0;top:0;z-index:10001;"], /*#__PURE__*/rgba('black', 0.75));
var NotificationWrapper = /*#__PURE__*/_styled(motion.div).withConfig({
  displayName: "indexstyled__NotificationWrapper",
  componentId: "bhctxx-1"
})(["position:absolute;right:50%;top:50%;transform:translate(50%,-50%);"]);
var NotificationInner = /*#__PURE__*/_styled(motion.section).withConfig({
  displayName: "indexstyled__NotificationInner",
  componentId: "bhctxx-2"
})(["display:flex;flex-direction:column;pointer-events:all;width:", ";z-index:1;@media ", "{width:100%;}"], 600 / 16 + "em", devices.mobile);
var NotificationHeader = /*#__PURE__*/_styled.header.withConfig({
  displayName: "indexstyled__NotificationHeader",
  componentId: "bhctxx-3"
})(["align-items:center;background-color:#a22825;color:white;display:flex;font-size:", ";height:", ";justify-content:space-between;padding:0 0 0 ", ";"], 18 / 16 + "em", 40 / 18 + "em", 10 / 18 + "em");
var NotificationClose = /*#__PURE__*/_styled.button.withConfig({
  displayName: "indexstyled__NotificationClose",
  componentId: "bhctxx-4"
})(["background-color:transparent;border:0;font-size:inherit;height:", ";margin:0;outline:0;padding:0;position:relative;width:", ";::after,::before{background-color:white;content:'';height:60%;position:absolute;right:50%;top:50%;transform:translate(50%,-50%) rotate(45deg);width:", ";}::before{transform:translate(50%,-50%) rotate(-45deg);}"], 40 / 18 + "em", 40 / 18 + "em", 4 / 18 + "em");
var NotificationBody = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__NotificationBody",
  componentId: "bhctxx-5"
})(["background-color:#313d41;color:white;padding:", ";word-break:break-word;"], 10 / 16 + "em");

var Notification$1 = function Notification$1(_ref) {
  var title = _ref.title,
      children = _ref.children;

  var _useNotification = useNotification(),
      isOpen = _useNotification.isOpen,
      close = _useNotification.close;

  var ref = useRef(null);
  var onClick = useCallback(function () {
    close();
  }, [close]);
  useOnClickOutside(ref, onClick);
  return React.createElement(AnimatePresence, null, isOpen && React.createElement(Notification, {
    ref: ref,
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  }, React.createElement(NotificationWrapper, null, React.createElement(NotificationInner, {
    initial: {
      y: -100
    },
    animate: {
      y: 0
    },
    exit: {
      y: -100
    }
  }, React.createElement(NotificationHeader, null, title, React.createElement(NotificationClose, {
    onClick: onClick
  })), React.createElement(NotificationBody, null, children)))));
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var runtime_1 = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  IteratorPrototype[iteratorSymbol] = function () {
    return this;
  };

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = Gp.constructor = GeneratorFunctionPrototype;
  GeneratorFunctionPrototype.constructor = GeneratorFunction;
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  AsyncIterator.prototype[asyncIteratorSymbol] = function () {
    return this;
  };
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  Gp[iteratorSymbol] = function() {
    return this;
  };

  Gp.toString = function() {
    return "[object Generator]";
  };

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
   module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  Function("r", "regeneratorRuntime = r")(runtime);
}
});

var SESSION = 'X-ODDS-SESSION';
var http = /*#__PURE__*/axios.create({
  baseURL: "" + (process.env.NODE_ENV !== "production" ? 'https://test.lionsbet.com/' : process.env.DOMAIN_URL)
});
var https = {
  get: function get(url, session) {
    var _ref;

    return http.get(url, {
      headers: _extends({}, session && (_ref = {}, _ref[SESSION] = session, _ref))
    });
  },
  post: function post(url, data, session) {
    var _headers;

    return http.post(url, data, {
      headers: (_headers = {}, _headers[SESSION] = session, _headers)
    });
  }
};

var getLocale = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(lang) {
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", https.get("/static/lang2/" + lang + ".json", ''));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function getLocale(_x) {
    return _ref.apply(this, arguments);
  };
}();

var fetchDictionary = /*#__PURE__*/createAsyncThunk('Dictionary/FetchDictionary', /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(locale, _ref) {
    var rejectWithValue, _yield$getLocale, data;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            rejectWithValue = _ref.rejectWithValue;
            _context.next = 3;
            return getLocale(locale);

          case 3:
            _yield$getLocale = _context.sent;
            data = _yield$getLocale.data;
            return _context.abrupt("return", data || rejectWithValue(null));

          case 6:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x, _x2) {
    return _ref2.apply(this, arguments);
  };
}());
var dictionary = /*#__PURE__*/createSlice({
  name: 'Dictionary',
  initialState: {},
  reducers: {},
  extraReducers: function extraReducers(builder) {
    builder.addCase(fetchDictionary.fulfilled, function (_, _ref3) {
      var payload = _ref3.payload;
      return payload;
    });
  }
});
var dictionary$1 = dictionary.reducer;

var getDictionary = function getDictionary(_ref) {
  var dictionary = _ref.dictionary;
  return dictionary;
};

var queryOptions = {
  refetchOnWindowFocus: false
};

var LocaleProvider = function LocaleProvider(_ref) {
  var locale = _ref.locale,
      children = _ref.children;
  var dispatch = useDispatch();
  var dictionary = useSelector(getDictionary);
  var dictionaryFetcher = useCallback(function () {
    return dispatch(fetchDictionary(locale));
  }, [locale]);
  useQuery('locale', dictionaryFetcher, queryOptions);
  if (isEmpty(dictionary)) return null;
  intl.init({
    currentLocale: 'default',
    locales: {
      "default": dictionary
    }
  });
  return React.createElement(React.Fragment, null, children);
};

var getGamesList = function getGamesList(session) {
  return /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(isMobile) {
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (isMobile === void 0) {
                isMobile = false;
              }

              return _context.abrupt("return", https.get("/rest/integration/onextwo/game_list?isMobile=" + isMobile, session));

            case 2:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }();
};
var getGame = function getGame(session) {
  return /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(_ref2) {
      var _ref2$isMobile, isMobile, _ref2$isRealPlay, isRealPlay, gameId;

      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _ref2$isMobile = _ref2.isMobile, isMobile = _ref2$isMobile === void 0 ? true : _ref2$isMobile, _ref2$isRealPlay = _ref2.isRealPlay, isRealPlay = _ref2$isRealPlay === void 0 ? false : _ref2$isRealPlay, gameId = _ref2.gameId;
              return _context2.abrupt("return", https.get("/rest/integration/onextwo/get_game?gameId=" + gameId + "&realPlay=" + isRealPlay + "&isMobile=" + isMobile + "&lobbyUrl=''", session));

            case 2:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function (_x2) {
      return _ref3.apply(this, arguments);
    };
  }();
};
var getBetmanGame = function getBetmanGame(session) {
  return /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(_ref6) {
      var _ref6$isMobile, isMobile, _ref6$isRealPlay, isRealPlay, gameId;

      return runtime_1.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              _ref6$isMobile = _ref6.isMobile, isMobile = _ref6$isMobile === void 0 ? true : _ref6$isMobile, _ref6$isRealPlay = _ref6.isRealPlay, isRealPlay = _ref6$isRealPlay === void 0 ? false : _ref6$isRealPlay, gameId = _ref6.gameId;
              return _context4.abrupt("return", https.get("/rest/integration/betman/get_game?gameId=" + gameId + "&realPlay=" + isRealPlay + "&isMobile=" + isMobile, session));

            case 2:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));

    return function (_x4) {
      return _ref7.apply(this, arguments);
    };
  }();
};
var getGoldenRaceData = function getGoldenRaceData(session) {
  return /*#__PURE__*/function () {
    var _ref9 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(_ref8) {
      var device, demo, countryCode;
      return runtime_1.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              device = _ref8.device, demo = _ref8.demo, countryCode = _ref8.countryCode;
              return _context5.abrupt("return", https.get("/rest/integration/goldenrace/get_game/" + device + "/?demo=" + demo + "&countryCode=" + countryCode, session));

            case 2:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5);
    }));

    return function (_x5) {
      return _ref9.apply(this, arguments);
    };
  }();
};

var gamesAdapter = /*#__PURE__*/createEntityAdapter({
  selectId: function selectId(_ref) {
    var game_id = _ref.game_id;
    return game_id;
  }
});
var fetchGames = /*#__PURE__*/createAsyncThunk('OnextwoGames/FetchGames', /*#__PURE__*/function () {
  var _ref4 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(_ref2, _ref3) {
    var isMobile, session, rejectWithValue, _yield$getGamesList, data;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            isMobile = _ref2.isMobile, session = _ref2.session;
            rejectWithValue = _ref3.rejectWithValue;
            _context.next = 4;
            return getGamesList(session)(isMobile);

          case 4:
            _yield$getGamesList = _context.sent;
            data = _yield$getGamesList.data;
            return _context.abrupt("return", (data == null ? void 0 : data.data) || rejectWithValue(gamesAdapter.getInitialState()));

          case 7:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x, _x2) {
    return _ref4.apply(this, arguments);
  };
}());
var games = /*#__PURE__*/createSlice({
  name: 'OnextwoGames',
  initialState: /*#__PURE__*/gamesAdapter.getInitialState(),
  reducers: {},
  extraReducers: function extraReducers(builder) {
    builder.addCase(fetchGames.fulfilled, function (state, _ref5) {
      var payload = _ref5.payload;
      var games = JSON.parse(payload);
      gamesAdapter.setAll(state, games);
    });
  }
});
var onextwo = games.reducer;

var getStreamsList = function getStreamsList(session) {
  return /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee() {
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", https.get("/rest/integrations/sis/get-streams", session));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
};

var streamAdapter = /*#__PURE__*/createEntityAdapter({
  selectId: function selectId(_ref) {
    var streamId = _ref.streamId;
    return streamId;
  }
});
var fetchStreams = /*#__PURE__*/createAsyncThunk('SisStreams/FetchStreams', /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(session, _ref2) {
    var rejectWithValue, _yield$getStreamsList, data;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            rejectWithValue = _ref2.rejectWithValue;
            _context.next = 3;
            return getStreamsList(session)();

          case 3:
            _yield$getStreamsList = _context.sent;
            data = _yield$getStreamsList.data;
            return _context.abrupt("return", (data == null ? void 0 : data.data) || rejectWithValue(streamAdapter.getInitialState()));

          case 6:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function (_x, _x2) {
    return _ref3.apply(this, arguments);
  };
}());
var streams = /*#__PURE__*/createSlice({
  name: 'SisStreams',
  initialState: /*#__PURE__*/streamAdapter.getInitialState(),
  reducers: {},
  extraReducers: function extraReducers(builder) {
    builder.addCase(fetchStreams.fulfilled, function (state, _ref4) {
      var payload = _ref4.payload;
      streamAdapter.setAll(state, payload);
    });
  }
});
var sis = streams.reducer;

var reducer = /*#__PURE__*/combineReducers({
  dictionary: dictionary$1,
  onextwo: onextwo,
  sis: sis
});
var store = /*#__PURE__*/configureStore({
  reducer: reducer,
  devTools: {
    name: 'SB-integrations'
  }
});

var _gamesAdapter$getSele = /*#__PURE__*/gamesAdapter.getSelectors(function (_ref) {
  var onextwo = _ref.onextwo;
  return onextwo;
}),
    selectAll = _gamesAdapter$getSele.selectAll;

var groupByGroupName = /*#__PURE__*/createSelector(selectAll, function (games) {
  return groupBy(games, function (_ref2) {
    var group_name = _ref2.group_name;
    return group_name;
  });
});

var GROUP_SIZE_DEFAULT_MOBILE = 2;
var GROUP_SIZE_DEFAULT_DESKTOP = 5;

var _templateObject;
var Wrapper = /*#__PURE__*/_styled.main.withConfig({
  displayName: "indexstyled__Wrapper",
  componentId: "a85wfd-0"
})(["background-color:white;border-radius:", ";box-shadow:2px 2px 2px 2px black;font-size:16px;margin:0 auto;max-width:", ";padding:1em;@media ", "{box-shadow:none;padding:0;}"], 10 / 16 + "em", 960 / 16 + "em", devices.mobile);
var GroupBar = /*#__PURE__*/_styled.header.withConfig({
  displayName: "indexstyled__GroupBar",
  componentId: "a85wfd-1"
})(["align-items:center;background-color:#32b85f;border-radius:", ";color:white;display:flex;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;font-weight:bold;justify-content:space-between;padding:", ";text-transform:uppercase;@media ", "{border-radius:0;}"], 5 / 18 + "em", 16 / 18 + "em", devices.mobile);
var Group = /*#__PURE__*/_styled.section.withConfig({
  displayName: "indexstyled__Group",
  componentId: "a85wfd-2"
})(["font-size:16px;&,*,*::before,*::after{box-sizing:border-box;}& + &{margin-top:", ";}"], 5 / 16 + "em");
var GroupBody = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__GroupBody",
  componentId: "a85wfd-3"
})(["margin:0 10%;padding:", " 0;@media ", "{margin:0 ", ";}"], 5 / 16 + "em", devices.mobile, 5 / 16 + "em");
var Row = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__Row",
  componentId: "a85wfd-4"
})(["display:flex;flex-wrap:wrap;margin:0 ", ";"], "-" + 5 / 16 + "em");
var Column = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__Column",
  componentId: "a85wfd-5"
})(["flex-basis:20%;max-width:20%;padding:", ";@media ", "{flex-basis:50%;max-width:50%;}"], 5 / 16 + "em", devices.mobile);
var ToggleText = /*#__PURE__*/_styled.span.withConfig({
  displayName: "indexstyled__ToggleText",
  componentId: "a85wfd-6"
})(["align-items:center;border-right:1px solid black;display:flex;justify-content:center;padding:0 ", ";@media ", "{display:none;}"], 16 / 12 + "em", devices.mobile);
var ToggleNumber = /*#__PURE__*/_styled(ToggleText).withConfig({
  displayName: "indexstyled__ToggleNumber",
  componentId: "a85wfd-7"
})(["border-right:none;@media ", "{display:flex;}"], devices.mobile);
var Toggle = /*#__PURE__*/_styled.button.withConfig({
  displayName: "indexstyled__Toggle",
  componentId: "a85wfd-8"
})(["align-items:stretch;background-color:white;border:1px solid #6ebb44;border-radius:", ";color:black;display:flex;font-size:12px;height:", ";margin:0;outline:0;padding:0 ", ";transition:all 0.15s linear;:hover{background-color:#2c8048;border-color:#32b85f;color:white;", "{border-color:white;}}"], 5 / 12 + "em", 32 / 12 + "em", 8 / 12 + "em", ToggleText);
var Game = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__Game",
  componentId: "a85wfd-9"
})(["display:flex;flex-direction:column;font-size:16px;"]);
var GameName = /*#__PURE__*/_styled.span.withConfig({
  displayName: "indexstyled__GameName",
  componentId: "a85wfd-10"
})(["align-items:center;background-color:#052040;border:1px solid #052447;color:#e7f3ff;display:flex;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:", ";font-weight:bold;height:", ";justify-content:center;margin:0 0 1px;text-align:center;"], 12 / 16 + "em", 32 / 12 + "em");

var rotate = /*#__PURE__*/keyframes(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  0% {\n    transform: translate(50%, -50%) rotate(0);\n  }\n  100% {\n    transform: translate(50%, -50%) rotate(360deg);\n  }\n"])));

var GameImageWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__GameImageWrapper",
  componentId: "a85wfd-11"
})(["padding-bottom:100%;position:relative;width:100%;::before{background-color:#32b85f;content:'';height:100%;position:absolute;right:0;top:0;width:100%;}::after{animation:1s ", " linear infinite;border:", " solid #1ea04a;border-radius:50%;border-right-color:#50c778;content:'';height:", ";position:absolute;right:50%;top:50%;width:", ";}"], rotate, 8 / 16 + "em", 32 / 16 + "em", 32 / 16 + "em");
var GameImage = /*#__PURE__*/_styled.img.withConfig({
  displayName: "indexstyled__GameImage",
  componentId: "a85wfd-12"
})(["height:100%;object-fit:cover;position:absolute;right:0;top:0;width:100%;z-index:1;"]);
var GameButtons = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__GameButtons",
  componentId: "a85wfd-13"
})(["background-color:#32b85f;display:flex;flex-direction:row;height:", ";"], 40 / 16 + "em");
var GameButton = /*#__PURE__*/_styled.button.withConfig({
  displayName: "indexstyled__GameButton",
  componentId: "a85wfd-14"
})(["background-color:#1c9344;border:0;border-radius:", ";color:white;flex-basis:50%;font-size:14px;margin:", ";max-width:50%;outline:0;overflow:hidden;padding:0;text-overflow:ellipsis;transition:all 0.15s linear;:hover,:focus{color:#fff000;}"], 4 / 14 + "em", 5 / 14 + "em");

var Game$1 = function Game$1(_ref) {
  var game_id = _ref.game_id,
      name = _ref.name,
      image = _ref.image;
  var session = useSelector(function (_ref2) {
    var session = _ref2.session;
    return session;
  });
  var isMobile = useIsMobile();
  var isAuth = useIsAuth();

  var _useNotification = useNotification(),
      open = _useNotification.open;

  var openGame = useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(_ref3) {
      var isRealPlay, _yield$getGame, data;

      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              isRealPlay = _ref3.isRealPlay;
              _context.next = 3;
              return getGame(session)({
                isMobile: isMobile,
                isRealPlay: isRealPlay,
                gameId: game_id
              });

            case 3:
              _yield$getGame = _context.sent;
              data = _yield$getGame.data;
              (data == null ? void 0 : data.data) && window.open(data.data, 'new', 'resizable,scrollbars,status');

            case 6:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref4.apply(this, arguments);
    };
  }(), [game_id, session]);
  var onPlayClick = useCallback(function () {
    return isAuth ? openGame({
      isRealPlay: true
    }) : open();
  }, [isAuth, openGame, open]);

  var onDemoClick = function onDemoClick() {
    return openGame({
      isRealPlay: false
    });
  };

  return React.createElement(Game, null, React.createElement(GameName, null, name), React.createElement(GameImageWrapper, null, React.createElement(GameImage, {
    src: image,
    alt: name
  })), React.createElement(GameButtons, null, React.createElement(GameButton, {
    onClick: onPlayClick
  }, intl.get('integration_play') || 'integration_play'), React.createElement(GameButton, {
    onClick: onDemoClick
  }, intl.get('integration_demo') || 'integration_demo')));
};

var Group$1 = function Group$1(_ref) {
  var group = _ref.group,
      games = _ref.games;
  var isMobile = useIsMobile();
  var defaultSize = useMemo(function () {
    return isMobile ? GROUP_SIZE_DEFAULT_MOBILE : GROUP_SIZE_DEFAULT_DESKTOP;
  }, [isMobile]);

  var _useState = useState(function () {
    return defaultSize;
  }),
      size = _useState[0],
      setSize = _useState[1];

  var _useState2 = useState(function () {
    return games.length - defaultSize;
  }),
      noOfHiddenGames = _useState2[0];

  var isToggleVisible = useMemo(function () {
    return noOfHiddenGames > 0;
  }, [noOfHiddenGames]);

  var onClick = function onClick() {
    return setSize(!size ? defaultSize : undefined);
  };

  return React.createElement(Group, null, React.createElement(GroupBar, null, group.replace(/([a-z])([A-Z])/g, '$1 $2'), isToggleVisible && React.createElement(Toggle, {
    onClick: onClick
  }, React.createElement(ToggleText, null, intl.get('integration_viewMore') || 'integration_viewMore'), React.createElement(ToggleNumber, null, "+" + noOfHiddenGames))), React.createElement(GroupBody, null, React.createElement(Row, null, games.slice(0, size).map(function (game) {
    return React.createElement(Column, {
      key: game.game_id
    }, React.createElement(Game$1, Object.assign({}, game)));
  }))));
};

var queryOptions$1 = {
  refetchOnWindowFocus: false
};

var GameList = function GameList() {
  var dispatch = useDispatch();
  var isMobile = useIsMobile();
  var session = useSession();
  var games = useSelector(groupByGroupName);
  var gamesFetcher = useCallback(function () {
    return dispatch(fetchGames({
      isMobile: isMobile,
      session: session
    }));
  }, [isMobile, session]);
  useQuery('onextwoGames', gamesFetcher, queryOptions$1);
  return React.createElement(React.Fragment, null, React.createElement(Notification$1, {
    title: intl.get('integration_notLoggedIn_title') || 'integration_notLoggedIn_title'
  }, intl.get('integration_notLoggedIn_body') || 'integration_notLoggedIn_body'), React.createElement(Wrapper, null, Object.keys(games).map(function (group, key) {
    return React.createElement(Group$1, {
      key: key,
      group: group,
      games: games[group]
    });
  })));
};

var IframeContainer = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__IframeContainer",
  componentId: "sc-17gimxf-0"
})(["height:0;min-height:100px;position:relative;overflow:hidden;height:100vh;height:", ";@media ", "{height:", ";}"], function (_ref) {
  var iframeHeight = _ref.iframeHeight;
  return iframeHeight ? "calc(" + iframeHeight + " + 100px)" : 0;
}, devices.mobile, function (_ref2) {
  var iframeHeight = _ref2.iframeHeight;
  return iframeHeight;
});
var Iframe = /*#__PURE__*/_styled.iframe.withConfig({
  displayName: "indexstyled__Iframe",
  componentId: "sc-17gimxf-1"
})(["position:absolute;top:0;left:0;height:100%;border:0;width:100%;"]);

var defaultColor = '#123abc';
var LoaderWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "indexstyled__LoaderWrapper",
  componentId: "naf60p-0"
})([""]);
var RotateLoader = /*#__PURE__*/_styled(RotateSpinLoader).withConfig({
  displayName: "indexstyled__RotateLoader",
  componentId: "naf60p-1"
})(["font-size:", ";border-color:", ";border-left-color:", ";"], function (_ref) {
  var size = _ref.size;
  return "" + (size != null ? size : '3px');
}, function (_ref2) {
  var color = _ref2.color;
  return (color != null ? color : defaultColor) + "33";
}, function (_ref3) {
  var color = _ref3.color;
  return "" + (color != null ? color : defaultColor);
});

var Loader = function Loader(_ref) {
  var color = _ref.color,
      size = _ref.size,
      className = _ref.className;
  return React.createElement(LoaderWrapper, {
    className: "loader " + className
  }, React.createElement(RotateLoader, {
    size: size,
    color: color
  }));
};

var Loader$1 = /*#__PURE__*/memo(Loader);

var GAME_ID = '1';
var queryOptions$2 = {
  refetchOnWindowFocus: false
};

var Betman = function Betman(_ref) {
  var refreshBalance = _ref.refreshBalance;

  var _useState = useState(undefined),
      iframeUrl = _useState[0],
      setIframeUrl = _useState[1];

  var _useState2 = useState(undefined),
      iframeHeight = _useState2[0],
      setIframeHeight = _useState2[1];

  var _useState3 = useState(undefined),
      iframeContainerHeight = _useState3[0],
      setIframeContainerHeight = _useState3[1];

  var session = useSession();
  var isMobile = useIsMobile();
  var getGameUrl = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee() {
    var _yield$getBetmanGame, data;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getBetmanGame(session)({
              isMobile: isMobile,
              isRealPlay: !!session.length,
              gameId: GAME_ID
            });

          case 2:
            _yield$getBetmanGame = _context.sent;
            data = _yield$getBetmanGame.data;
            setIframeUrl(data == null ? void 0 : data.data);

          case 5:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [session, isMobile, session, setIframeUrl]);

  var calculateIframeContainerHeight = function calculateIframeContainerHeight(height) {
    isMobile ? setIframeContainerHeight(height) : setIframeContainerHeight(parseInt(height, 10) + 100 + "px");
  };

  var iframeCommunication = function iframeCommunication(event) {
    var _event$data;

    if (!(event != null && (_event$data = event.data) != null && _event$data.command)) {
      return;
    }

    if (event.data.command === 'adjustiframe') {
      setIframeHeight(event.data.height);
      calculateIframeContainerHeight(event.data.height);
    }

    if (event.data.command === 'refreshbalance') {
      refreshBalance();
    }
  };

  useQuery('betman', getGameUrl, _extends({}, queryOptions$2));
  useEffect(function () {
    window.addEventListener('message', iframeCommunication);
    return function () {
      return window.removeEventListener('message', iframeCommunication);
    };
  }, []);
  return React.createElement(React.Fragment, null, React.createElement(IframeContainer, {
    style: {
      height: iframeContainerHeight
    }
  }, iframeUrl ? React.createElement(Iframe, {
    id: "contentiframe",
    height: iframeHeight,
    src: iframeUrl,
    allowFullScreen: "true",
    webkitAllowFullScreen: "true",
    mozAllowFullScreen: "true",
    scrolling: "auto"
  }) : React.createElement(Loader$1, {
    color: "#F05A22"
  })));
};

var LOADING = "loading";
var IDLE = "idle";
var READY = "ready";
var ERROR = "error";
var LOAD = "load";

var useScript = function useScript(src, id) {
  var _useState = useState(src ? LOADING : IDLE),
      status = _useState[0],
      setStatus = _useState[1];

  useEffect(function () {
    if (!src) {
      setStatus(IDLE);
      return;
    }

    var script = document.querySelector("script[src=\"" + src + "\"]");

    if (!script) {
      script = document.createElement("script");
      script.id = id != null ? id : '';
      script.src = src;
      script.async = true;
      script.dataset.status = LOADING;
      document.body.appendChild(script);

      var setAttributeFromEvent = function setAttributeFromEvent(event) {
        if (!script) return;
        script.dataset.status = (event == null ? void 0 : event.type) === LOAD ? READY : ERROR;
      };

      script.addEventListener(LOAD, setAttributeFromEvent);
      script.addEventListener(ERROR, setAttributeFromEvent);
    } else {
      script.dataset.status && setStatus(script.dataset.status);
    }

    var setStateFromEvent = function setStateFromEvent(event) {
      setStatus((event == null ? void 0 : event.type) === LOAD ? READY : ERROR);
    };

    script.addEventListener(LOAD, setStateFromEvent);
    script.addEventListener(ERROR, setStateFromEvent);
    return function () {
      if (script) {
        script.removeEventListener(LOAD, setStateFromEvent);
        script.removeEventListener(ERROR, setStateFromEvent);
      }
    };
  }, [src]);
  return status;
};

var SCRIPT_ID_DESKTOP = 'golden-race-desktop-loader';
var SELECTOR_ID_DESKTOP = 'golden-race-desktop-app';
var SCRIPT_ID_MOBILE = 'golden-race-mobile-loader';
var SELECTOR_ID_MOBILE = 'golden-race-mobile-app';
var DEVICES = {
  desktop: 'WEB',
  mobile: 'MOBILE'
};
var LANGUAGES_MAP = {
  en: 'en-GB',
  es: 'es-ES',
  it: 'it-IT',
  fr: 'fr-BE',
  ja: 'ja-JP',
  ka: 'ka-GE',
  ko: 'ko-KR',
  ms: 'ms-MY',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ro: 'ro-RO',
  ru: 'ru-RU',
  th: 'th-TH',
  uk: 'uk-UA',
  vi: 'vi-VN',
  zh: 'zh-CN'
};

var IframeContainer$1 = /*#__PURE__*/_styled.div.attrs(function (_ref) {
  var isMobile = _ref.isMobile;
  return {
    id: isMobile ? SELECTOR_ID_MOBILE : SELECTOR_ID_DESKTOP
  };
}).withConfig({
  displayName: "indexstyled__IframeContainer",
  componentId: "sc-1jyb8rv-0"
})(["background:#fff;"]);

var GoldenRace = function GoldenRace(_ref) {
  var refreshBalance = _ref.refreshBalance,
      countryCode = _ref.countryCode,
      language = _ref.language,
      profile = _ref.profile;

  var _useState = useState({
    url_script: undefined,
    online_hash: undefined,
    hardware_id: undefined
  }),
      data = _useState[0],
      setData = _useState[1];

  var isMobile = useIsMobile();
  var scriptStatus = useScript(data.url_script, isMobile ? SCRIPT_ID_MOBILE : SCRIPT_ID_DESKTOP);
  var session = useSession();
  var refIframeContainer = useRef(null);
  var goldenRaceData = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee() {
    var _yield$getGoldenRaceD, data;

    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getGoldenRaceData(session)({
              device: isMobile ? DEVICES.mobile : DEVICES.desktop,
              demo: !session.length,
              countryCode: countryCode
            });

          case 2:
            _yield$getGoldenRaceD = _context.sent;
            data = _yield$getGoldenRaceD.data;
            setData(JSON.parse(data == null ? void 0 : data.data));

          case 5:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [session, isMobile, countryCode]);
  var settings = useMemo(function () {
    return _extends({
      language: LANGUAGES_MAP[language],
      profile: profile,
      onlineHash: data.online_hash,
      hwId: data.hardware_id
    }, !!refreshBalance && !isMobile && {
      onRefreshCredit: refreshBalance
    }, !!refreshBalance && isMobile && {
      onCreditUpdate: refreshBalance
    });
  }, [language, data, profile]);
  useEffect(function () {
    var _refIframeContainer$c, _refIframeContainer$c2;

    // Wait till script gets loaded
    if (scriptStatus !== READY) return; // Get rid of a previously set up iframe, if available

    (_refIframeContainer$c = refIframeContainer.current) == null ? void 0 : (_refIframeContainer$c2 = _refIframeContainer$c.firstChild) == null ? void 0 : _refIframeContainer$c2.remove();
    window.grLoader = window["gr" + (isMobile ? 'Mobile' : 'Desktop') + "Loader"](settings);
  }, [refIframeContainer.current, scriptStatus, settings]); // Kick-start GR

  useEffect(function () {
    goldenRaceData();
  }, [goldenRaceData]);
  if (scriptStatus !== READY) return React.createElement(Loader$1, {
    color: "#013be5"
  });
  return React.createElement(IframeContainer$1, {
    isMobile: isMobile,
    ref: refIframeContainer
  });
};

var Onextwo = function Onextwo(_ref) {
  var session = _ref.session,
      lang = _ref.lang,
      isMobile = _ref.isMobile,
      isAuth = _ref.isAuth;
  return React.createElement(Provider, {
    store: store
  }, React.createElement(SessionProvider, {
    session: session
  }, React.createElement(LocaleProvider, {
    locale: lang
  }, React.createElement(IsMobileProvider, {
    isMobile: isMobile
  }, React.createElement(IsAuthProvider, {
    isAuth: isAuth
  }, React.createElement(NotificationProvider, null, React.createElement(GameList, null)))))));
};

var BetmanGame = function BetmanGame(_ref2) {
  var session = _ref2.session,
      lang = _ref2.lang,
      isMobile = _ref2.isMobile,
      refreshBalance = _ref2.refreshBalance;
  return React.createElement(Provider, {
    store: store
  }, React.createElement(SessionProvider, {
    session: session
  }, React.createElement(LocaleProvider, {
    locale: lang
  }, React.createElement(IsMobileProvider, {
    isMobile: isMobile
  }, React.createElement(NotificationProvider, null, React.createElement(Betman, {
    refreshBalance: refreshBalance
  }))))));
};

var GoldenRaceGame = function GoldenRaceGame(_ref3) {
  var session = _ref3.session,
      lang = _ref3.lang,
      isMobile = _ref3.isMobile,
      countryCode = _ref3.countryCode,
      language = _ref3.language,
      profile = _ref3.profile,
      refreshBalance = _ref3.refreshBalance;
  return React.createElement(Provider, {
    store: store
  }, React.createElement(SessionProvider, {
    session: session
  }, React.createElement(LocaleProvider, {
    locale: lang
  }, React.createElement(IsMobileProvider, {
    isMobile: isMobile
  }, React.createElement(NotificationProvider, null, React.createElement(GoldenRace, {
    countryCode: countryCode,
    language: language,
    profile: profile,
    refreshBalance: refreshBalance
  }))))));
};

var SBIntegrations = {
  useSession: useSession$1,
  Onextwo: Onextwo,
  BetmanGame: BetmanGame,
  GoldenRaceGame: GoldenRaceGame
};

export { SBIntegrations };
//# sourceMappingURL=sb-integrations.esm.js.map
